/**
 * Sidebar Component
 * Side navigation menu with filtered items based on permissions
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  Popper,
  Paper,
  MenuList,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useRef } from 'react'; // ensure useRef is imported

import { NAV_CONFIG, filterNavByPermissions } from '../config/navigation';
import { useAuth } from '../hooks/useAuth';

const DRAWER_WIDTH = 100;

/**
 * NavItem Component
 * Single navigation menu item with stacked icon and text
 */
const NavItem = ({ item }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutRef = useRef(null);
  const open = Boolean(anchorEl);

  const normalizePath = (path = '') => {
    if (!path) return '';
    if (path === '/') return '/';
    return path.replace(/\\/g, '/').replace(/\/+$/, '');
  };
  const currentPath = normalizePath(location.pathname);

  const hasChildren = item.children && item.children.length > 0;
  const normalizedItemPath = normalizePath(item.path);
  const isActive = normalizedItemPath && (currentPath === normalizedItemPath || currentPath.startsWith(`${normalizedItemPath}/`));

  // Check if any child is active
  const hasActiveChild =
    hasChildren &&
    item.children.some((child) => {
      const childPath = normalizePath(child.path);
      const childActive =
        childPath &&
        (currentPath === childPath ||
          currentPath.startsWith(`${childPath}/`));

      const childRelatedMatch = child.relatedPaths?.some((pattern) => {
        if (!pattern) return false;
        const normalizedPattern = normalizePath(pattern);
        return (
          normalizedPattern &&
          (currentPath === normalizedPattern ||
            currentPath.startsWith(`${normalizedPattern}/`))
        );
      });

    return childActive || childRelatedMatch;
  });
  
  const matchesRelatedPath = item.relatedPaths?.some((pattern) => {
    if (!pattern) return false;
    const normalizedPattern = normalizePath(pattern);
    return normalizedPattern && (currentPath === normalizedPattern || currentPath.startsWith(`${normalizedPattern}/`));
  });
  const isSelected = isActive || hasActiveChild || matchesRelatedPath;

  const handleBoxEnter = (event) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (hasChildren) {
      // If anchorEl is not set, set it. Otherwise keep it as is.
      // (Actually event.currentTarget is always the Box, so it's safe to just set it)
      if (!anchorEl) {
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleBoxLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100); // 300ms allows the user to cross the gap safely
  };

  const handleClick = () => {
    if (!hasChildren && item.path) {
      navigate(item.path);
    }
  };

  const handleChildClick = (childPath) => {
    navigate(childPath);
    setAnchorEl(null);
  };

  const Icon = item.icon;

  return (
    <Box 
      onMouseEnter={handleBoxEnter} 
      onMouseLeave={handleBoxLeave}
      sx={{ display: 'flex', justifyContent: 'center' }}
    >
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        sx={{
          borderRadius: '12px',
          mb: 1.5,
          mx: 'auto',
          width: '80px', // slightly wider to fit wrapped text nicely
          // minHeight: '72px', // let it grow if text wraps
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 4px',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          backgroundColor: 'transparent',
          '&.Mui-selected': {
            backgroundColor: '#eff6ff', // Light blue background like Ace AI
            '&:hover': {
              backgroundColor: '#e0f2fe',
            },
          },
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 'auto', mb: 0.5 }}>
          <Icon
            sx={{
              fontSize: 24,
              color: isSelected ? theme.palette.primary.main : '#64748b',
              transition: 'all 0.2s ease',
            }}
          />
        </ListItemIcon>
        
        <ListItemText
          sx={{ m: 0, width: '100%' }}
          primary={
            <Typography
              variant="caption"
              align="center"
              sx={{
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? theme.palette.primary.main : '#475569',
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  maxWidth: '100%',
                  whiteSpace: 'normal', 
                  display: 'block',
                  transition: 'color 0.2s ease',
              }}
            >
              {item.label}
            </Typography>
          }
        />
      </ListItemButton>

      {hasChildren && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="right"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8], // 0px along main axis (vertical since placement is right), 8px away from anchor
              },
            },
          ]}
          style={{ zIndex: theme.zIndex.drawer + 2 }}
        >
          <Paper
            elevation={4}
            sx={{
               overflow: 'visible',
               filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
               borderRadius: '12px',
               minWidth: '160px',
               padding: '8px',
               border: '1px solid #e2e8f0',
               boxShadow: 'none',
               // CSS Arrow pointing left
               '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  left: -6,
                  width: 12,
                  height: 12,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  borderLeft: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
               },
            }}
          >
            <MenuList sx={{ p: 0, position: 'relative', zIndex: 1 }}>
              {item.children.map((child) => (
                 <MenuItem 
                    key={child.id} 
                    onClick={() => handleChildClick(child.path)}
                    sx={{ 
                      borderRadius: '8px',
                      mb: 0.5,
                      padding: '8px 12px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#1e293b',
                      transition: 'background-color 0.2s',
                      '&:last-child': { mb: 0 },
                      '&:hover': {
                        backgroundColor: '#f8fafc',
                        color: theme.palette.primary.main,
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.primary.main,
                        }
                      }
                    }}
                 >
                    {child.icon && (
                      <ListItemIcon sx={{ minWidth: '32px', color: '#64748b', transition: 'color 0.2s' }}>
                        <child.icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                    )}
                    {child.label}
                 </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      )}
    </Box>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    path: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    children: PropTypes.array,
  }).isRequired,
};

/**
 * Sidebar
 * Side navigation drawer with filtered menu items
 * 
 * @param {Object} props
 * @param {boolean} props.open - Sidebar open state
 * @param {Function} props.onClose - Callback to close sidebar (mobile)
 * @returns {React.ReactNode}
 */
const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter navigation based on user permissions
  const filteredNav = filterNavByPermissions(NAV_CONFIG, user?.permissions || []);

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        pt: 0,
        mt: 1,
           px: 0.1,
      }}
    >
      <Box 
        sx={{ 
          width: '100%',
          flexGrow: 1, 
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            display: 'none', // Hide scrollbar for a cleaner look
          },
        }}
      >
        <List component="nav" aria-label="main navigation" sx={{ pt: 0, px: 1 }}>
          {filteredNav.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}
        </List>
      </Box>
    </Box>
  );

  return isMobile ? (
    // Mobile: Temporary drawer
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          // borderRight: '1px solid #e2e8f0',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  ) : (
    // Desktop: Permanent drawer
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH : 0,
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          border:"none",
          boxShadow:"none",
          borderRight: 'none', // subtle border
          top: '64px', // Below header
          height: 'calc(100% - 64px)',
          zIndex: theme.zIndex.drawer - 1,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Sidebar;
export { DRAWER_WIDTH };
